import React, {useEffect, useState} from 'react';
import {Box, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {default as ListItem} from './request-list-item';
import InternalTemplate from '../../../../../components/templates/internal';
import {StackParamList} from '../../../../../navigators';
import {SpinnerModal} from '../../../../../components/modals';
import {EmptyListIcon} from '../../../../../components/icons';
import {IRequestsToSyncItem} from '../../../../../interfaces/dtos/requests-to-sync.interface';
import {useSalesmanContext} from '../../../../../context/salesman';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/buttons';
import {sleep} from '../../../../../utils/sleep';
import useToast from '../../../../../hooks';
import {StyleSheet} from 'react-native';
import {message as t} from '../../../../../utils/messages';

type ScreenProps = NativeStackScreenProps<
  StackParamList,
  'WithoutConnectionRequestList'
>;

type RequestToProcess = IRequestsToSyncItem & {
  status: 'processed' | 'sending' | 'waiting' | 'failed';
};

const WithoutConnectionRequestList = ({navigation}: ScreenProps) => {
  const {
    requestsToSync,
    clearRequestToServer,
    syncNewClientRequest,
    syncPaymentProofRequest,
    syncSalesOrderRequest,
    updateAllRequestToStorage,
  } = useSalesmanContext();

  const [requestsToShow, setRequestsToShow] = useState(
    [] as RequestToProcess[],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const {showSyncError} = useToast();

  const fillSortedRequest = () => {
    const all: IRequestsToSyncItem[] = requestsToSync
      ? [
          ...requestsToSync.customerRegistryList,
          ...requestsToSync.salesOrderList,
          ...requestsToSync.paymentReportList,
        ]
      : [];

    // Sort the combined array by `createdAt` in descending order
    const sorted = all.sort((a, b) => {
      const dateA = new Date(`${a.createdAt}`);
      const dateB = new Date(`${b.createdAt}`);
      return dateA.getTime() - dateB.getTime();
    });

    const list: RequestToProcess[] = sorted.map(item => {
      return {...item, status: 'waiting'};
    });

    setRequestsToShow(list);
  };

  useEffect(() => {
    setIsLoading(true);
    fillSortedRequest();
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoBack = () => {
    navigation.navigate('SalesmanDashboard');
  };

  function isOdd(num: number) {
    return num % 2;
  }

  const handleOnTapClear = async () => {
    setIsCleaning(true);
    await clearRequestToServer();
    await sleep(3000);
    setIsCleaning(false);
    setRequestsToShow([]);
  };

  const handleOnTapSyncList = async () => {
    setIsLoading(true);
    let showSynError = false;
    const list: RequestToProcess[] = [];
    try {
      requestsToShow.forEach(request => {
        request.status = 'waiting';
      });

      let requestsToProcess = [...requestsToShow];

      for (const toSync of requestsToProcess) {
        if (toSync.status === 'waiting') {
          let result = false;

          toSync.status = 'sending';
          setRequestsToShow([...requestsToProcess]);

          if (toSync.workflow === 'customer-registration') {
            result = await syncNewClientRequest(toSync);
          } else if (toSync.workflow === 'payment-proof') {
            result = await syncPaymentProofRequest(toSync);
          } else if (toSync.workflow === 'salesman-order') {
            result = await syncSalesOrderRequest(toSync);
          }

          toSync.status = result ? 'processed' : 'failed';

          if (!result) {
            list.push(toSync);
            showSynError = true;
          }
        }
      }
      setRequestsToShow(list);
      await updateAllRequestToStorage();
    } catch {
      showSynError = true;
    }
    setIsLoading(false);
    if (showSynError) {
      showSyncError();
    }
  };

  const handleOnTapFinish = () => {
    navigation.goBack();
  };

  const showList = () => {
    return requestsToShow.length > 0;
  };

  const showContent = () => {
    if (!showList()) {
      return (
        <VStack paddingX={5}>
          <EmptyListIcon customStyles={styles.emptyListIcon} />
          <Box mt={20}>
            <PrimaryButton
              label="screen-salesman-without-connection-finish-button"
              onPress={handleOnTapFinish}
            />
          </Box>
        </VStack>
      );
    }
    if (showList()) {
      return requestsToShow.map(
        (item: RequestToProcess, index: number) =>
          item.status !== 'processed' && (
            <ListItem
              key={`client-item-${index}`}
              onPress={() => {}}
              rowType={isOdd(index) ? 'odd' : 'even'}
              item={item}
              status={item.status}
            />
          ),
      );
    }

    return <></>;
  };

  return (
    <InternalTemplate
      title={'screen-salesman-without-connection-list'}
      contentPaddingX="0"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'back-dashboard',
        usage: 'external',
      }}>
      <SpinnerModal show={isLoading} title={t('syncronization-sending')} />
      <SpinnerModal show={isCleaning} title={t('syncronization-cleaning')} />
      <Box marginBottom={5} minHeight={100}>
        {showContent()}
      </Box>
      <VStack paddingX={5} paddingBottom={150}>
        {showList() && (
          <>
            <PrimaryButton
              label="screen-salesman-without-connection-send-button"
              onPress={handleOnTapSyncList}
            />
            <SecondaryButton
              label="screen-salesman-without-connection-clear-button"
              onPress={handleOnTapClear}
              mt="3"
            />
          </>
        )}
      </VStack>
    </InternalTemplate>
  );
};
export default WithoutConnectionRequestList;

const styles = StyleSheet.create({
  emptyListIcon: {width: 160, height: 160, alignSelf: 'center'},
});
